import type { ReactElement } from 'react';

import { NameOrSkeleton } from './NameOrSkeleton';
import { initialsFor, type ResolvedMemberName } from './utils';

interface TeamRowProps {
  readonly resolved: ResolvedMemberName;
  readonly role: string | null;
  readonly badge?: string;
}

/** One care-team member row in the panel — avatar + name + optional role/badge. */
export function TeamRow({ resolved, role, badge }: TeamRowProps): ReactElement {
  const pending = resolved.status === 'pending';
  return (
    <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-border last:border-b-0">
      {pending ? (
        <span
          aria-hidden="true"
          className="h-7 w-7 rounded-full bg-muted animate-pulse shrink-0"
        />
      ) : (
        <span
          aria-hidden="true"
          className="h-7 w-7 rounded-full bg-brand text-brand-foreground flex items-center justify-center text-[11px] font-semibold leading-none shrink-0"
        >
          {initialsFor(resolved.name)}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="text-[13px] font-medium text-foreground leading-tight truncate">
            <NameOrSkeleton resolved={resolved} />
          </p>
          {badge !== undefined && (
            <span className="text-[10px] font-semibold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded bg-primary/10 text-primary shrink-0">
              {badge}
            </span>
          )}
        </div>
        {role !== null && (
          <p className="text-[11.5px] text-muted-foreground mt-0.5">{role}</p>
        )}
      </div>
    </div>
  );
}
